(() => {
  "use strict";

  if (window.__AnywellAssistantBootstrapped) return;
  window.__AnywellAssistantBootstrapped = true;

  const SOURCE_POLICY_ID = "storefront-product-meta-minimum-data-v1";
  const RECORDING_POLICY_VERSION = "cs-service-analysis-20260905";
  const REQUEST_TIMEOUT_MS = 12000;
  const MAX_RESPONSE_BYTES = 256 * 1024;
  const MAX_HISTORY_ITEMS = 6;
  const MAX_RETRY_AFTER_SECONDS = 300;
  const DEFAULT_RETRY_AFTER_SECONDS = 5;
  const RETRY_TIMER_BUFFER_MS = 50;
  const PURCHASE_SURFACE_SELECTORS = [
    ".anywell-sticky-purchase.is-visible",
    "product-quick-add.is-visible",
  ];
  const PURCHASE_SURFACE_TARGET_SELECTOR = ".anywell-sticky-purchase, product-quick-add";
  const PRODUCTS = [
    { key: "x024", title: "X-024", image: "anywell-product-x024.jpg", imageData: "x024" },
    { key: "ya12-5", title: "YA12-5", image: "anywell-product-ya12.jpg", imageData: "ya125" },
    { key: "ys12-15-plus", title: "YS12-15 PLUS", image: "anywell-product-ys12.webp", imageData: "ys1215Plus" },
    { key: "ys16-1-max", title: "YS16-1 MAX", image: "anywell-product-ys16.jpg", imageData: "ys161Max" },
  ];
  const PENDING_QUESTION_IDS = new Set([
    null,
    "primary_priority",
    "loading_support",
    "seat_or_compact",
    "trip_distance",
    "primary_surface",
    "doorway_clearance",
    "ys16_battery_variant",
    "support_product_model",
    "support_observation",
  ]);
  const PENDING_SUPPORT_TOPIC_IDS = new Set([
    null,
    "assembly_setup",
    "repair_parts",
    "power_startup",
    "airline_travel",
    "payment_methods",
    "multiple_support_topics",
  ]);
  const COPY = {
    "en-US": {
      privacy:
        "For your privacy, please remove personal or order details. I can continue with general product questions or route you to secure support.",
      secureSupport:
        "You can contact Anywell through its official support page. This chat does not book a call or send your details.",
      medical:
        "I can explain verified product specifications. A qualified clinician can assess suitability for your condition; this chat cannot confirm personal safety.",
      discrepancy:
        "A product or listing discrepancy needs human review. Please use secure support and identify the exact model or public listing there.",
      priceMismatch:
        "A price mismatch needs a current human review. I won’t guess which price or promotion should apply.",
      dynamicUnavailable:
        "I can’t confirm the live value right now. You can open the product page or ask for human help.",
      offline: "You appear to be offline. Reconnect to check current product information.",
      transportFailure: "I couldn’t complete that check. Please try again.",
      timeout:
        "That check took too long, so I stopped it. Please try again; your question was not stored.",
      cancelled: "Check cancelled. Your question was not stored.",
      rateLimited: "Too many requests arrived at once. Please wait briefly, then try again.",
      withheld: "Sensitive details withheld on this device",
      medicalSummary: "Medical, legal, or personal safety suitability review requested.",
      discrepancySummary: "Possible product or public-listing specification discrepancy.",
      priceMismatchSummary: "Current price or promotion mismatch review requested.",
      secureSupportSummary:
        "Order, account, payment, refund, or customer-specific support requested.",
      privacySummary:
        "Personal, order, payment, contact, address, or account details were withheld before processing.",
      handoffHeading: "Contact Anywell",
      handoffDefaultSummary: "Your conversation is not sent automatically.",
      handoffConsentAria: "Open Anywell's official support page",
      handoffConsent: "I want to open the official support page. My chat will not be sent.",
      openSecureSupport: "Open secure support",
      secureSupportNotConnected: "Secure support not connected",
      editQuestion: "Ask a general product question",
      retry: "Retry",
      retryAfterConnection: "Retry after connection",
      cancel: "Cancel",
      replyPending: "Anywell AI is replying...",
      replyTools: "Answer options",
      readAloud: "Read aloud on this device",
      stopReading: "Stop reading",
      localVoiceUnavailable: "On-device reading is unavailable in this browser",
      copyAnswer: "Copy answer",
      copiedAnswer: "Answer copied. You can share it with someone you trust.",
      copyUnavailable: "Copying is unavailable. You can select the answer text instead.",
      repeatAnswer: "Repeat answer",
      repeatedAnswer: "Repeated answer",
      productConfirmed: "Product confirmed: {product}.",
      imageFormat: "Use a PNG, JPEG, or WebP product image.",
      imageTooLarge: "That image is over 6 MB. Please choose a smaller product screenshot.",
      imagePrivacy:
        "Image stays in this tab and is not uploaded or auto-recognized. Choose the matching model yourself.",
      confirmPicturedModel:
        "Confirm the pictured model first so I don’t answer for the wrong product.",
      chooseProduct: "Show me which product you mean, then I can answer precisely.",
      pickerAria: "Choose a product",
      pickerTitle: "Which product do you mean?",
      pickerDetail: "Choose a photo so I answer for the right model.",
      pickerClose: "Close",
      pickerCloseAria: "Close product chooser",
      verifiedConnection: "Verified Anywell guidance",
      serviceReadyConnection: "Service ready · verifies every answer",
      verifiedStableSource: "Verified product facts",
      verifiedFreshSource: "Verified current product data",
      unavailableSource: "Current information not verified",
      feedbackAria: "Was this answer helpful?",
      feedbackPrompt: "Helpful?",
      feedbackYes: "Yes",
      feedbackNo: "Not quite",
      feedbackThanks: "Thanks — feedback stays on this page.",
      feedbackSaved: "Thanks. Your feedback was saved with this answer.",
      feedbackFailed: "Feedback was not confirmed saved. You can try again.",
      feedbackSaving: "Saving feedback...",
      recordingSummary: "Protected chats saved for service analysis: 90 days",
      recordingSummaryOn: "Protected chats saved for service analysis: 90 days",
      recordingNotice: "Anywell saves protected messages and feedback for 90 days for service analysis and support quality. You can stop saving below.",
      recordingPrivacy: "Do not share personal, order, payment, account, or health details. Images stay in this tab. Earlier messages are never added.",
      recordingLifetime: "You can stop saving or delete this tab's saved chat here. After refresh or navigation, this tab loses its deletion access.",
      recordingResume: "Resume saving",
      recordingOff: "Saving is off. You can continue chatting without saving.",
      recordingStarting: "Preparing protected chat history...",
      recordingSaving: "Saving is on for new protected messages and feedback.",
      recordingStopped: "Future saving is off. Earlier saved messages remain until deleted or expired.",
      recordingUnavailable: "Saving could not be confirmed. Future questions will use chat without saving. Earlier messages may remain saved.",
      recordingOmitted: "This answer's sensitive content was omitted from saving. Other protected messages may still be saved.",
      recordingEventFailed: "The answer is available, but its display or cancellation record could not be confirmed saved.",
      recordingStop: "Stop saving",
      recordingDelete: "Delete this saved chat",
      recordingDeletePrompt: "Delete saved messages from this tab and stop saving? Backups may retain deleted data for up to 30 days.",
      recordingDeleteConfirm: "Delete saved messages",
      recordingKeep: "Keep saved messages",
      recordingDeleting: "Saving stopped. Deleting this tab's saved messages...",
      recordingDeleted: "This tab's saved chat was deleted from active records. Backups may retain it for up to 30 days.",
      recordingDeleteFailed: "Deletion could not be confirmed. Saving is off. Keep this tab open and try deleting again.",
      recordingInterrupted: "The check stopped. Some messages may already be saved. You can delete this saved chat below.",
      recordingTransportFailure: "I could not confirm that check. It was not retried automatically. Some messages may be saved; you can delete this saved chat.",
      openProduct: "Open product",
      chooseProductAction: "Choose product",
      compareBoth: "Compare both",
      range12Question: "What is the rated range for the 12Ah YS16-1 MAX?",
      range20Question: "What is the rated range for the 20Ah YS16-1 MAX?",
      compareBothQuestion: "Compare the 12Ah and 20Ah YS16-1 MAX variants.",
      offlineConnection: "Offline · live facts unavailable",
      notConfiguredConnection: "Product guidance ready · AI not connected",
      transportConnection: "Service unavailable · no answer generated",
      rateLimitedConnection: "Request limit reached · retry shortly",
      timedOutConnection: "Service timed out · try again",
      cancelledConnection: "Check cancelled · service not reverified",
    },
    "zh-CN": {
      privacy: "为保护你的隐私，请移除个人信息或订单详情。我可以继续回答一般商品问题，或引导你联系安全人工支持。",
      secureSupport: "您可以通过 Anywell 官方联系页联系人工。本会话不会预约回电，也不会自动发送您的聊天内容。",
      medical: "我可以说明已核验的商品规格，但不能评估医疗适用性，也不能判断某款助行产品是否适合特定健康状况。",
      discrepancy: "商品实物与页面信息不一致时，需要人工核查。请联系安全支持，并说明具体型号或公开商品页面。",
      priceMismatch: "价格或促销不一致时，需要人工核查当前信息。我不会猜测应适用的价格或优惠。",
      dynamicUnavailable: "目前无法核验实时信息。你可以打开商品页面，或联系人工支持。",
      offline: "你当前似乎处于离线状态。重新连接后可核验当前商品信息。",
      transportFailure: "这次核验未能完成，请重试。",
      timeout: "本次核验用时过长，已停止请求。请重试；你的问题不会被保存。",
      cancelled: "已取消核验。你的问题不会被保存。",
      rateLimited: "请求过于频繁，请稍候再重试。",
      withheld: "敏感信息已在此设备上隐藏",
      medicalSummary: "需要人工评估医疗、法律或个人安全适用性。",
      discrepancySummary: "可能存在商品实物或公开页面规格不一致。",
      priceMismatchSummary: "需要人工核查当前价格或促销信息。",
      secureSupportSummary: "需要处理订单、账户、付款、退款或其他客户专属问题。",
      privacySummary: "个人、订单、付款、联系、地址或账户信息已在处理前隐藏。",
      handoffHeading: "联系 Anywell",
      handoffDefaultSummary: "聊天内容不会自动发送。",
      handoffConsentAria: "打开 Anywell 官方联系页",
      handoffConsent: "我希望打开官方联系页。不会自动发送我的聊天内容。",
      openSecureSupport: "打开安全人工支持",
      secureSupportNotConnected: "安全人工支持尚未连接",
      editQuestion: "改问一般商品问题",
      retry: "重试",
      retryAfterConnection: "连接恢复后重试",
      cancel: "取消",
      replyPending: "Anywell AI 正在回复...",
      replyTools: "回答选项",
      readAloud: "在此设备上朗读",
      stopReading: "停止朗读",
      localVoiceUnavailable: "此浏览器暂不支持设备本地朗读",
      copyAnswer: "复制回答",
      copiedAnswer: "回答已复制，可以分享给您信任的人。",
      copyUnavailable: "暂时无法复制，您可以选择回答文字。",
      repeatAnswer: "再说一遍",
      repeatedAnswer: "重复的回答",
      productConfirmed: "已确认商品：{product}。",
      imageFormat: "请使用 PNG、JPEG 或 WebP 格式的商品图片。",
      imageTooLarge: "图片超过 6 MB，请选择较小的商品截图。",
      imagePrivacy: "图片仅保留在此标签页，不会上传或自动识别。请自行选择对应型号。",
      confirmPicturedModel: "请先确认图片中的商品型号，以免我针对错误商品作答。",
      chooseProduct: "请先选择你指的商品，我才能准确回答。",
      pickerAria: "选择商品",
      pickerTitle: "你指的是哪款商品？",
      pickerDetail: "请选择商品图片，以便我针对正确型号作答。",
      pickerClose: "关闭",
      pickerCloseAria: "关闭商品选择器",
      verifiedConnection: "Anywell 商品信息已核验",
      serviceReadyConnection: "服务已就绪 · 每次回答均会核验",
      verifiedStableSource: "已核验产品事实",
      verifiedFreshSource: "已核验当前商品信息",
      unavailableSource: "当前信息未能核验",
      feedbackAria: "这个回答有帮助吗？",
      feedbackPrompt: "这个回答有帮助吗？",
      feedbackYes: "有帮助",
      feedbackNo: "还不太对",
      feedbackThanks: "谢谢。反馈只留在当前页面，不会保存个人信息。",
      feedbackSaved: "谢谢。反馈已与这条回答一起保存。",
      feedbackFailed: "未能确认反馈已保存，你可以重试。",
      feedbackSaving: "正在保存反馈…",
      recordingSummary: "脱敏对话保存 90 天，用于售前分析",
      recordingSummaryOn: "脱敏对话保存 90 天，用于售前分析",
      recordingNotice: "Anywell 将保护处理后的消息和反馈保存 90 天，用于售前分析和服务质量复核。你可以在下方停止保存。",
      recordingPrivacy: "请勿提供个人、订单、付款、账户或健康详情。图片仅留在此标签页。之前的消息不会补录。",
      recordingLifetime: "你可以在此停止保存或删除本标签页已保存的聊天。刷新或跳转后，本标签页将失去删除凭证。",
      recordingResume: "恢复保存",
      recordingOff: "保存已关闭，你可以继续不保存地咨询。",
      recordingStarting: "正在准备保护处理后的聊天记录…",
      recordingSaving: "保存已开启，仅记录之后保护处理后的消息和反馈。",
      recordingStopped: "已停止后续保存。之前保存的消息将保留至删除或到期。",
      recordingUnavailable: "未能确认保存结果。之后的问题将不保存；之前的消息可能已保存。",
      recordingOmitted: "本次回答的敏感内容已从保存记录中省略，其他保护处理后的消息仍可能保存。",
      recordingEventFailed: "回答仍可查看，但未能确认其展示或取消记录已保存。",
      recordingStop: "停止保存",
      recordingDelete: "删除这次已保存的聊天",
      recordingDeletePrompt: "删除本标签页已保存的消息并停止保存？备份中删除的数据最多可能保留 30 天。",
      recordingDeleteConfirm: "确认删除已保存的消息",
      recordingKeep: "保留已保存的消息",
      recordingDeleting: "已停止保存，正在删除本标签页保存的消息…",
      recordingDeleted: "本标签页的聊天已从活动记录中删除，备份中最多可能保留 30 天。",
      recordingDeleteFailed: "未能确认删除成功，保存已关闭。请保留此标签页并再次尝试删除。",
      recordingInterrupted: "核验已停止，部分消息可能已经保存。你可以在下方删除这次已保存的聊天。",
      recordingTransportFailure: "未能确认这次核验的结果，因此没有自动重复请求。部分消息可能已经保存，你可以删除这次已保存的聊天。",
      openProduct: "打开商品",
      chooseProductAction: "选择商品",
      compareBoth: "比较两款电池",
      range12Question: "YS16-1 MAX 12Ah 版本的额定续航是多少？",
      range20Question: "YS16-1 MAX 20Ah 版本的额定续航是多少？",
      compareBothQuestion: "请比较 YS16-1 MAX 的 12Ah 和 20Ah 版本。",
      offlineConnection: "当前离线 · 无法核验实时信息",
      notConfiguredConnection: "商品指导可用 · AI 尚未连接",
      transportConnection: "服务暂时不可用 · 未生成回答",
      rateLimitedConnection: "请求过于频繁 · 请稍候重试",
      timedOutConnection: "服务响应超时 · 请重试",
      cancelledConnection: "核验已取消 · 服务尚未重新核验",
    },
  };

  const PII_RULES = [
    {
      type: "email",
      detect: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,
      replace: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
      token: "[EMAIL]",
    },
    {
      type: "payment_number",
      detect: /\b(?:\d[ -]*?){13,19}\b/,
      replace: /\b(?:\d[ -]*?){13,19}\b/g,
      token: "[PAYMENT_NUMBER]",
    },
    {
      type: "phone",
      detect: /(?:\+?\d[\s().-]*){8,}/,
      replace: /(?:\+?\d[\s().-]*){8,}/g,
      token: "[PHONE]",
    },
    {
      type: "order_reference",
      detect:
        /(?:\border(?:\s+(?:number|no\.?|id|reference))?\s*[:#-]?\s*[A-Z-]*\d[A-Z0-9-]{2,}\b|订单(?:号|编号)?\s*[:#：-]?\s*[A-Z0-9-]*\d[A-Z0-9-]{2,})/i,
      replace:
        /(?:\border(?:\s+(?:number|no\.?|id|reference))?\s*[:#-]?\s*[A-Z-]*\d[A-Z0-9-]{2,}\b|订单(?:号|编号)?\s*[:#：-]?\s*[A-Z0-9-]*\d[A-Z0-9-]{2,})/gi,
      token: "[ORDER_REFERENCE]",
    },
    {
      type: "account_reference",
      detect:
        /(?:\baccount(?:\s+(?:number|no\.?|id|reference))\s*[:#-]?\s*[A-Z0-9-]{3,}\b|账户(?:号|编号)?\s*[:#：-]?\s*[A-Z0-9-]{3,})/i,
      replace:
        /(?:\baccount(?:\s+(?:number|no\.?|id|reference))\s*[:#-]?\s*[A-Z0-9-]{3,}\b|账户(?:号|编号)?\s*[:#：-]?\s*[A-Z0-9-]{3,})/gi,
      token: "[ACCOUNT_REFERENCE]",
    },
    {
      type: "postal_address",
      detect:
        /\b\d{1,6}\s+[\w.'-]+(?:\s+[\w.'-]+){0,4}\s+(?:street|st|road|rd|avenue|ave|lane|ln|drive|dr|boulevard|blvd|way|court|ct)\b/i,
      replace:
        /\b\d{1,6}\s+[\w.'-]+(?:\s+[\w.'-]+){0,4}\s+(?:street|st|road|rd|avenue|ave|lane|ln|drive|dr|boulevard|blvd|way|court|ct)\b/gi,
      token: "[POSTAL_ADDRESS]",
    },
    {
      type: "date_of_birth",
      detect:
        /(?:\b(?:date\s+of\s+birth|birth\s*date|dob|born\s+on)\s*(?:is\s*)?[:#-]?\s*(?:\d{1,2}[/-]){1,2}\d{2,4}\b|出生(?:日期|年月日)?\s*[:：-]?\s*\d{2,4}年\d{1,2}月\d{1,2}日)/i,
      replace:
        /(?:\b(?:date\s+of\s+birth|birth\s*date|dob|born\s+on)\s*(?:is\s*)?[:#-]?\s*(?:\d{1,2}[/-]){1,2}\d{2,4}\b|出生(?:日期|年月日)?\s*[:：-]?\s*\d{2,4}年\d{1,2}月\d{1,2}日)/gi,
      token: "[DATE_OF_BIRTH]",
    },
    {
      type: "exact_age",
      detect:
        /(?:\b(?:i\s*(?:am|'m)|my\s+age\s+is|age\s*[:=-]?)\s*(?:is\s*)?(?:1[01]\d|\d{1,2})\s*(?:years?\s*old|y\/?o)\b|我(?:今年)?\s*(?:1[01]\d|\d{1,2})\s*岁|年龄\s*[:：-]?\s*(?:1[01]\d|\d{1,2}))/i,
      replace:
        /(?:\b(?:i\s*(?:am|'m)|my\s+age\s+is|age\s*[:=-]?)\s*(?:is\s*)?(?:1[01]\d|\d{1,2})\s*(?:years?\s*old|y\/?o)\b|我(?:今年)?\s*(?:1[01]\d|\d{1,2})\s*岁|年龄\s*[:：-]?\s*(?:1[01]\d|\d{1,2}))/gi,
      token: "[EXACT_AGE]",
    },
    {
      type: "gender_identity",
      detect:
        /(?:\b(?:i\s*(?:am|'m)|gender\s*[:=-]?|sex\s*[:=-]?)\s*(?:female|male|woman|man|non[- ]?binary)\b|性别\s*[:：-]?\s*(?:男|女|非二元))/i,
      replace:
        /(?:\b(?:i\s*(?:am|'m)|gender\s*[:=-]?|sex\s*[:=-]?)\s*(?:female|male|woman|man|non[- ]?binary)\b|性别\s*[:：-]?\s*(?:男|女|非二元))/gi,
      token: "[GENDER_IDENTITY]",
    },
    {
      type: "medical_detail",
      detect:
        /(?:\b(?:(?:i\s+)?was\s+diagnosed\s+with|i\s+have|diagnosis\s*[:=-]?|medical\s+condition\s*[:=-]?)\s*(?:arthritis|diabetes|parkinson(?:'s)?|multiple\s+sclerosis|copd|stroke|heart\s+(?:disease|failure)|cancer)\b|(?:我)?(?:被)?确诊(?:为|了)?[一-鿿]{1,20}|(?:我)?患有[一-鿿]{1,20})/i,
      replace:
        /(?:\b(?:(?:i\s+)?was\s+diagnosed\s+with|i\s+have|diagnosis\s*[:=-]?|medical\s+condition\s*[:=-]?)\s*(?:arthritis|diabetes|parkinson(?:'s)?|multiple\s+sclerosis|copd|stroke|heart\s+(?:disease|failure)|cancer)\b|(?:我)?(?:被)?确诊(?:为|了)?[一-鿿]{1,20}|(?:我)?患有[一-鿿]{1,20})/gi,
      token: "[MEDICAL_DETAIL]",
    },
    {
      type: "person_name",
      detect:
        /(?:\b(?:my\s+name\s+is|full\s+name\s*[:=-]?)\s*[A-Z][A-Za-z'-]{1,30}(?:\s+[A-Z][A-Za-z'-]{1,30}){0,2}\b|我叫[一-鿿]{2,4}|姓名\s*[:：-]?\s*[一-鿿]{2,4})/i,
      replace:
        /(?:\b(?:my\s+name\s+is|full\s+name\s*[:=-]?)\s*[A-Z][A-Za-z'-]{1,30}(?:\s+[A-Z][A-Za-z'-]{1,30}){0,2}\b|我叫[一-鿿]{2,4}|姓名\s*[:：-]?\s*[一-鿿]{2,4})/gi,
      token: "[PERSON_NAME]",
    },
  ];

  function randomHex(bytes = 16) {
    const values = new Uint8Array(bytes);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(values);
    } else {
      for (let index = 0; index < values.length; index += 1) {
        values[index] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(values, (value) => value.toString(16).padStart(2, "0")).join("");
  }

  function normalizeLocale(value) {
    return String(value || "").toLowerCase().startsWith("zh") ? "zh-CN" : "en-US";
  }

  function containsCjk(value) {
    return /[\u3400-\u9fff]/.test(String(value || ""));
  }

  function localeForMessage(value, assistantRoot) {
    const message = String(value || "").trim();
    if (!message) return normalizeLocale(assistantRoot && assistantRoot.dataset.locale);
    return containsCjk(message) ? "zh-CN" : "en-US";
  }

  function copyForLocale(locale) {
    return COPY[normalizeLocale(locale)];
  }

  function boundedRetryAfterSeconds(value) {
    const raw = String(value == null ? "" : value).trim();
    if (!/^(?:\d+|\d*\.\d+)$/.test(raw)) return DEFAULT_RETRY_AFTER_SECONDS;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return DEFAULT_RETRY_AFTER_SECONDS;
    return Math.min(MAX_RETRY_AFTER_SECONDS, Math.max(1, Math.ceil(parsed)));
  }

  function replyDelayMilliseconds(random = Math.random) {
    return 5000 + Math.min(5000, Math.floor(random() * 5001));
  }

  function shouldDelayReply(response) {
    return (response.outcome === "answered" || response.outcome === "clarify") &&
      !response.handoff &&
      response.conversation_context?.last_topic_id !== "urgent_safety_incident" &&
      !(response.fact_freshness?.dynamic_query_required && !response.fact_freshness.dynamic_query_succeeded);
  }

  function waitForReplyPresentation(deadline, signal) {
    if (signal.aborted) return Promise.reject(new DOMException("Reply cancelled", "AbortError"));
    const remaining = Math.max(0, deadline - performance.now());
    if (!remaining) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const abort = () => {
        window.clearTimeout(timer);
        signal.removeEventListener("abort", abort);
        reject(new DOMException("Reply cancelled", "AbortError"));
      };
      const timer = window.setTimeout(() => {
        signal.removeEventListener("abort", abort);
        resolve();
      }, remaining);
      signal.addEventListener("abort", abort, { once: true });
    });
  }

  function answerParagraphs(text) {
    const segmenter = typeof Intl.Segmenter === "function"
      ? new Intl.Segmenter(undefined, { granularity: "sentence" }) : null;
    const result = [];
    for (const block of text.split(/\n+/).filter((part) => part.trim())) {
      const sentences = segmenter ? Array.from(segmenter.segment(block), (item) => item.segment.trim()) : [block.trim()];
      let paragraph = "";
      let count = 0;
      for (const sentence of sentences) {
        if (paragraph && (paragraph.length + sentence.length > 140 || count === 2)) {
          result.push(paragraph);
          paragraph = "";
          count = 0;
        }
        paragraph += (paragraph ? " " : "") + sentence;
        count += 1;
      }
      if (paragraph) result.push(paragraph);
    }
    return result;
  }

  function appendHighlightedText(parent, text) {
    const numbers = /\b(?:return authorization|original packaging|buyer-paid|seller-paid|no restocking fee|two-year limited warranty|no home pickup)\b|\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}(?:,?\s+\d{4})?\b|\$\d[\d,]*(?:\.\d+)?(?:\s*USD)?|\b\d{4}-\d{2}-\d{2}\b|\b\d[\d,]*(?:\.\d+)?(?:\s*(?:%|USD|Ah|lbs?|kg|mm|cm|inches|inch|miles|mile|days|day|years|year|hours|hour|磅|英寸|英里|天|年))?/gi;
    let index = 0;
    for (const match of text.matchAll(numbers)) {
      if (match.index > 0 && /[A-Za-z-]/.test(text[match.index - 1])) continue;
      parent.appendChild(document.createTextNode(text.slice(index, match.index)));
      const strong = document.createElement("strong");
      strong.textContent = match[0];
      parent.appendChild(strong);
      index = match.index + match[0].length;
    }
    parent.appendChild(document.createTextNode(text.slice(index)));
  }

  function localReadingVoice(voices, locale) {
    const language = locale.toLowerCase().split("-")[0];
    return voices.find((voice) => voice.localService === true && voice.lang.toLowerCase().split(/[-_]/)[0] === language) || null;
  }

  function repeatAnswerText(text) {
    return text.replace(/\s*(?:Would you like me to repeat that\?|需要我再说一遍吗？)\s*$/, "");
  }

  function isSubstantiveReply(response) {
    const topic = response.conversation_context && response.conversation_context.last_topic_id;
    return response.outcome === "answered" && topic !== "social_conversation";
  }

  function replyShareText(response) {
    const parts = [repeatAnswerText(response.answer_text)];
    const checked = response.fact_freshness && response.fact_freshness.checked_at;
    if (checked) parts.push("Checked: " + checked);
    const links = new Set();
    for (const action of response.ui_actions || []) {
      const target = safePublicTarget(action.public_target);
      if (target && /^\/(products|policies)\/[a-z0-9-]+(?:[?#]|$)/i.test(target)) {
        links.add("https://anywellshop.com" + target.split(/[?#]/)[0]);
      }
    }
    return [...parts, ...links].join("\n\n");
  }

  /* Lucide icons (volume-2, copy, rotate-ccw), ISC License.
   * Copyright (c) 2026 Lucide Icons and Contributors.
   * Permission to use, copy, modify, and/or distribute this software for any
   * purpose with or without fee is hereby granted, provided that the above
   * copyright notice and this permission notice appear in all copies.
   * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
   * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
   * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
   * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
   * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
   * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
   * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
   */
  function replyToolIcon(name) {
    const shapes = {
      read: [["path", { d: "M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" }], ["path", { d: "M16 9a5 5 0 0 1 0 6" }], ["path", { d: "M19.364 18.364a9 9 0 0 0 0-12.728" }]],
      copy: [["rect", { width: "14", height: "14", x: "8", y: "8", rx: "2", ry: "2" }], ["path", { d: "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" }]],
      repeat: [["path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }], ["path", { d: "M3 3v5h5" }]],
    };
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    for (const [attribute, value] of Object.entries({ viewBox: "0 0 24 24", width: "20", height: "20", fill: "none", stroke: "currentColor", "stroke-width": "2", "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false" })) svg.setAttribute(attribute, value);
    for (const [tag, attributes] of shapes[name]) {
      const shape = document.createElementNS("http://www.w3.org/2000/svg", tag);
      for (const [attribute, value] of Object.entries(attributes)) shape.setAttribute(attribute, value);
      svg.appendChild(shape);
    }
    return svg;
  }

  function screenMessage(raw) {
    let redacted = raw;
    const piiTypes = [];

    PII_RULES.forEach((rule) => {
      if (rule.detect.test(redacted)) {
        piiTypes.push(rule.type);
        redacted = redacted.replace(rule.replace, rule.token);
      }
    });

    return { redacted, piiTypes: [...new Set(piiTypes)] };
  }

  function restrictedIntent(text, locale) {
    const normalized = text.toLowerCase();
    const copy = copyForLocale(locale);

    if (
      /\b(medical suitability|diagnos(?:e|is) me|prescription required|safe for me|safe for my|suitable for my health)\b/.test(
        normalized,
      ) ||
      /医疗适用|给我诊断|需要处方|适合我的身体|对我安全吗/.test(text)
    ) {
      return {
        code: "MEDICAL_LEGAL_SAFETY",
        answer: copy.medical,
        summary: copy.medicalSummary,
      };
    }

    if (
      /\b(delivered|received|listing|spec(?:ification)?).{0,36}\b(different|mismatch|heavier|lighter|wrong|doesn.?t match)\b/.test(
        normalized,
      ) ||
      /实物|收到的|到货.{0,12}不一致|不符/.test(text)
    ) {
      return {
        code: "SPEC_DISCREPANCY",
        answer: copy.discrepancy,
        summary: copy.discrepancySummary,
      };
    }

    if (
      /\b(price|promotion|discount).{0,30}\b(mismatch|different|wrong|honou?r|apply)\b/.test(normalized) ||
      /价格|促销|折扣.{0,12}不一致|不对/.test(text)
    ) {
      return {
        code: "PRICE_MISMATCH",
        answer: copy.priceMismatch,
        summary: copy.priceMismatchSummary,
      };
    }

    if (
      /\b(my order|order status|my refund|refund status|compensation|my payment|payment failed|my account|return my|my return|warranty claim)\b/.test(
        normalized,
      ) ||
      /我的订单|订单状态|我的退款|退款进度|赔偿|付款失败|我的账户|售后申请/.test(text)
    ) {
      return {
        code: "ORDER_OR_ACCOUNT",
        answer: copy.secureSupport,
        summary: copy.secureSupportSummary,
      };
    }

    return null;
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (typeof text === "string") node.textContent = text;
    return node;
  }

  function safeSameOriginEndpoint(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (!["http:", "https:"].includes(url.protocol)) return null;
      return url.href;
    } catch (_error) {
      return null;
    }
  }

  function safeServiceEndpoint(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.username || url.password) return null;
      const hasSafeTransport =
        url.origin === window.location.origin || url.protocol === "https:";
      if (!hasSafeTransport) return null;
      return url.href;
    } catch (_error) {
      return null;
    }
  }

  function safeRecordingEndpoint(value, serviceEndpoint) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      const allowedOrigins = new Set([window.location.origin]);
      if (serviceEndpoint) allowedOrigins.add(new URL(serviceEndpoint).origin);
      const local = url.origin === window.location.origin && ["localhost", "127.0.0.1"].includes(url.hostname);
      if (!allowedOrigins.has(url.origin) || (url.protocol !== "https:" && !(local && url.protocol === "http:"))) return null;
      if (url.username || url.password || url.search || url.hash || !/^\/api\/customer-service\/v2\/?$/.test(url.pathname)) return null;
      return url.origin + "/api/customer-service/v2";
    } catch (_error) {
      return null;
    }
  }

  function safeRecordingProductContext(context) {
    const handles = {
      "x024": ["anywell-x-024-wide-seat-electric-mobility-scooter"],
      "ya12-5": ["anywell-ya12-5-long-range-durable-electric-wheelchair"],
      "ys12-15-plus": ["anywell-ys12-15-plus-folding-electric-wheelchair"],
      "ys16-1-max": ["ys16-1-max-heavy-duty-wide-seat-power-wheelchair-12ah", "anywell-ys16-max-wide-seat-electric-wheelchair-20ah"],
    };
    const approved = context.product_key ? handles[context.product_key] || [] : Object.values(handles).flat();
    return { ...context, page_handle: approved.includes(context.page_handle) ? context.page_handle : null };
  }

  async function recordingPost(base, route, payload, signal) {
    if (!["sessions", "chat", "events", "feedback", "delete"].includes(route)) throw new Error("INVALID_RECORDING_ROUTE");
    const controller = signal ? null : new AbortController();
    const timer = controller ? window.setTimeout(() => controller.abort(), 8000) : null;
    try {
      const response = await fetch(base + "/" + route, {
        method: "POST", credentials: "omit", cache: "no-store", redirect: "error", referrerPolicy: "no-referrer",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(payload), signal: signal || controller.signal,
      });
      if (!response.ok) {
        const error = new Error("RECORDING_REQUEST_FAILED");
        if (response.status === 429) {
          error.code = "RATE_LIMITED";
          error.retryAfterSeconds = boundedRetryAfterSeconds(response.headers.get("Retry-After"));
        }
        throw error;
      }
      if (!String(response.headers.get("Content-Type") || "").toLowerCase().startsWith("application/json")) throw new Error("INVALID_CONTENT_TYPE");
      if (Number(response.headers.get("Content-Length") || 0) > MAX_RESPONSE_BYTES) throw new Error("RESPONSE_TOO_LARGE");
      const body = await response.text();
      if (new TextEncoder().encode(body).byteLength > MAX_RESPONSE_BYTES) throw new Error("RESPONSE_TOO_LARGE");
      return JSON.parse(body);
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  function createRecordingClient(base, notify) {
    const sessions = new Set();
    const receipts = new Map();
    let active = null;
    let version = 0;
    let pending = false;
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const owns = (ref) => ref && sessions.has(ref.session) && ref.session === active;
    const credentials = (session) => ({ conversation_id: session.conversation_id, token: session.token });

    function waitForDisplay(ref) {
      ref.displayReady = new Promise((resolve) => { ref.resolveDisplay = resolve; });
    }

    function stopRecording() {
      active = null;
      for (const ref of receipts.values()) ref.resolveDisplay(false);
    }

    function sendRecordingEvent(ref, type, elapsedMs, holdMs) {
      if (!owns(ref) || !["displayed", "cancelled"].includes(type) || (ref.terminal && ref.terminal !== type)) return Promise.resolve(false);
      if (ref.eventConfirmed) return Promise.resolve(true);
      if (ref.eventPromise) return ref.eventPromise;
      const retry = Boolean(ref.terminal);
      if (retry) waitForDisplay(ref);
      ref.terminal = type;
      if (type === "cancelled") ref.resolveDisplay(false);
      if (!ref.eventPayload) {
        const validTiming = Number.isFinite(elapsedMs) && Number.isFinite(holdMs) &&
          0 <= holdMs && holdMs <= elapsedMs && elapsedMs <= 3600000;
        // Long-hidden tabs still report display or cancellation, without invented latency.
        ref.eventPayload = {
          ...credentials(ref.session), message_id: ref.messageId, type,
          elapsed_ms: validTiming ? Math.round(elapsedMs) : null,
          hold_ms: validTiming ? Math.round(holdMs) : null,
        };
      }
      // A retry repeats only the same metadata, never the question or model call.
      ref.eventPromise = Promise.resolve().then(async () => {
        let confirmed = false;
        try {
          if (!owns(ref)) return false;
          const result = await recordingPost(base, "events", ref.eventPayload);
          if (!result || result.status !== "saved") throw new Error("EVENT_NOT_SAVED");
          confirmed = true;
          ref.eventConfirmed = true;
          if (retry && owns(ref)) notify("recordingSaving");
        } catch (_error) {
          if (owns(ref)) notify("recordingEventFailed");
        } finally {
          ref.eventPromise = null;
          ref.resolveDisplay(type === "displayed" && confirmed);
        }
        return confirmed;
      });
      return ref.eventPromise;
    }

    return {
      isSaving: () => active !== null,
      hasSaved: () => sessions.size > 0,
      isPending: () => pending,
      receipt: (requestId) => receipts.get(requestId) || null,
      canFeedback: (ref) => Boolean(owns(ref) && ref.terminal !== "cancelled"),
      async start(locale, deviceClass) {
        if (pending || active) return false;
        const operation = ++version;
        pending = true;
        notify("recordingStarting");
        try {
          const session = await recordingPost(base, "sessions", {
            policy_version: RECORDING_POLICY_VERSION, locale, device_class: deviceClass,
          });
          if (!session || !uuid.test(session.conversation_id) || !/^[A-Za-z0-9_-]{32,256}$/.test(session.token) ||
              session.retention_days !== 90 || session.policy_version !== RECORDING_POLICY_VERSION) throw new Error("INVALID_RECORDING_SESSION");
          sessions.add(session);
          if (operation === version) active = session;
          notify(active ? "recordingSaving" : "recordingStopped");
          return Boolean(active);
        } catch (_error) {
          notify("recordingUnavailable");
          return false;
        } finally {
          pending = false;
        }
      },
      stop() {
        version += 1;
        stopRecording();
        notify("recordingStopped");
      },
      async chat(request, signal) {
        const session = active;
        if (!session) throw new Error("RECORDING_NOT_ACTIVE");
        try {
          // Keep the known product/variant context, but never backfill earlier tab history.
          const envelope = await recordingPost(base, "chat", {
            ...credentials(session),
            request: { ...request, recent_history: [], product_context: safeRecordingProductContext(request.product_context) },
          }, signal);
          if (!envelope || !envelope.reply) throw new Error("INVALID_RECORDING_RESPONSE");
          const saved = envelope.recording;
          if (saved && ["saved", "content-omitted"].includes(saved.status) &&
              saved.conversation_id === session.conversation_id && uuid.test(saved.message_id) && sessions.has(session)) {
            const ref = { session, messageId: saved.message_id, terminal: null, eventPromise: null, eventConfirmed: false, eventPayload: null };
            waitForDisplay(ref);
            receipts.set(request.request_id, ref);
            if (active === session) notify(saved.status === "content-omitted" ? "recordingOmitted" : "recordingSaving");
          } else {
            if (active === session) stopRecording();
            notify("recordingUnavailable");
          }
          return envelope.reply;
        } catch (error) {
          if (active === session) stopRecording();
          notify("recordingUnavailable");
          throw error;
        }
      },
      async event(ref, type, elapsedMs, holdMs) {
        return sendRecordingEvent(ref, type, elapsedMs, holdMs);
      },
      async feedback(ref, helpful) {
        if (!owns(ref) || typeof helpful !== "boolean") throw new Error("FEEDBACK_NOT_AUTHORIZED");
        if (ref.terminal === "cancelled") throw new Error("ANSWER_NOT_DISPLAYED");
        if (ref.terminal === "displayed" && !ref.eventConfirmed && !ref.eventPromise) {
          sendRecordingEvent(ref, "displayed", ref.eventPayload.elapsed_ms, ref.eventPayload.hold_ms);
        }
        const displayed = ref.eventConfirmed || await ref.displayReady;
        if (!displayed || !owns(ref)) throw new Error("ANSWER_NOT_DISPLAYED");
        const result = await recordingPost(base, "feedback", {
          ...credentials(ref.session), message_id: ref.messageId, helpful,
        });
        if (!result || result.status !== "saved") throw new Error("FEEDBACK_NOT_SAVED");
        return true;
      },
      async remove() {
        if (pending) return false;
        version += 1;
        stopRecording();
        pending = true;
        notify("recordingDeleting");
        let removed = true;
        for (const session of [...sessions]) {
          try {
            const result = await recordingPost(base, "delete", credentials(session));
            if (!result || result.deleted !== true) throw new Error("DELETE_NOT_CONFIRMED");
            sessions.delete(session);
            for (const [id, ref] of receipts) if (ref.session === session) receipts.delete(id);
          } catch (_error) {
            removed = false;
          }
        }
        pending = false;
        notify(removed ? "recordingDeleted" : "recordingDeleteFailed");
        return removed;
      },
    };
  }

  function safeSupportUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol === "https:") return url.href;
      if (
        url.origin === window.location.origin &&
        url.protocol === "http:" &&
        ["localhost", "127.0.0.1"].includes(url.hostname)
      ) {
        return url.href;
      }
    } catch (_error) {
      return null;
    }
    return null;
  }

  function safePublicTarget(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (!["http:", "https:"].includes(url.protocol)) return null;
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return null;
    }
  }

  function validateResponse(response, requestId) {
    if (!response || typeof response !== "object") throw new Error("INVALID_RESPONSE");
    if (response.schema_version !== 1 || response.request_id !== requestId) {
      throw new Error("INVALID_RESPONSE");
    }
    if (typeof response.answer_text !== "string" || !response.product_context) {
      throw new Error("INVALID_RESPONSE");
    }
    if (
      !response.privacy ||
      response.privacy.input_redacted_before_core !== true ||
      response.privacy.raw_input_persisted !== false ||
      response.privacy.customer_identity_persisted !== false
    ) {
      throw new Error("UNSAFE_RESPONSE");
    }
    if (
      !response.guidance_context ||
      !Array.isArray(response.guidance_context.need_ids) ||
      !Array.isArray(response.guidance_context.answer_ids) ||
      !response.guidance ||
      !Array.isArray(response.guidance.next_options) ||
      !response.conversation_context ||
      typeof response.conversation_context.last_topic_id !== "string" ||
      !PENDING_QUESTION_IDS.has(response.conversation_context.pending_question_id) ||
      !PENDING_SUPPORT_TOPIC_IDS.has(response.conversation_context.pending_support_topic_id) ||
      !Number.isInteger(response.conversation_context.repeat_count) ||
      !Number.isInteger(response.conversation_context.turn_index) ||
      !response.generation ||
      !["ai", "deterministic"].includes(response.generation.mode) ||
      !["openai", "none"].includes(response.generation.provider) ||
      response.generation.stored !== false
    ) {
      throw new Error("INVALID_RESPONSE");
    }
    return response;
  }

  function buildShell(root) {
    if (root.querySelector("[data-aw-ai-panel]")) return;
    root.classList.add("aw-ai");
    const template = document.createElement("template");
    template.innerHTML = `
      <button class="aw-ai__launcher" type="button" data-aw-ai-open aria-label="Ask Anywell" title="Ask Anywell" aria-expanded="false" aria-haspopup="dialog">
        <span class="aw-ai__launcher-icon" aria-hidden="true">?</span>
        <span class="aw-ai__launcher-label">Ask Anywell</span>
      </button>
      <button class="aw-ai__backdrop" type="button" data-aw-ai-close aria-label="Close assistant" hidden></button>
      <section class="aw-ai__panel" data-aw-ai-panel role="dialog" aria-modal="false" hidden>
        <div class="aw-ai__trustbar">OFFICIAL ANYWELL PRODUCT SUPPORT</div>
        <header class="aw-ai__header">
          <img class="aw-ai__avatar" data-aw-ai-support-avatar alt="" width="44" height="44">
          <span class="aw-ai__heading">
            <strong data-aw-ai-title tabindex="-1">Anywell Help</strong>
            <span data-aw-ai-subtitle>Online product guidance</span>
          </span>
          <button class="aw-ai__icon-button" type="button" data-aw-ai-minimize aria-label="Minimize assistant" title="Minimize assistant">&#8722;</button>
          <button class="aw-ai__icon-button aw-ai__icon-button--close" type="button" data-aw-ai-close aria-label="Close assistant" title="Close assistant">&#215;</button>
        </header>
        <div class="aw-ai__connection" data-aw-ai-connection>
          <span class="aw-ai__connection-dot" aria-hidden="true"></span>
          <span data-aw-ai-connection-label>Product guidance</span>
        </div>
        <div class="aw-ai__conversation" data-aw-ai-conversation tabindex="-1">
          <button class="aw-ai__current-product" type="button" data-aw-ai-current-product hidden>
            <img data-aw-ai-current-product-image alt="" hidden>
            <span><small>You are viewing</small><strong data-aw-ai-current-product-title></strong></span>
          </button>
          <div class="aw-ai__message aw-ai__message--assistant">
            <span class="aw-ai__message-label">Anywell AI Customer Service</span>
            <div class="aw-ai__bubble"><p data-aw-ai-welcome>Hello, I'm Anywell's AI customer-service assistant. I can check current prices and availability, and help with product questions. How can I help you today?</p></div>
          </div>
          <section class="aw-ai__faq-entry" aria-label="Popular questions">
            <div class="aw-ai__faq-heading"><strong>Popular questions</strong></div>
            <div class="aw-ai__suggestions" data-aw-ai-suggestions aria-label="Popular customer questions">
              <button type="button" data-aw-ai-question="Help me choose the right model for my needs.">Help me choose a model</button>
              <button type="button" data-aw-ai-question="How much does this model weigh, and is it practical to load into a car?">Weight &amp; car transport</button>
              <button type="button" data-aw-ai-question="What is the current price and availability of this model?">Price &amp; availability</button>
              <button type="button" data-aw-ai-question="How far can this model travel on a charge?">Travel range</button>
            </div>
            <button class="aw-ai__product-link" type="button" data-aw-ai-show-products>Not sure which model? Choose a product</button>
          </section>
          <section class="aw-ai__product-picker" data-aw-ai-product-picker hidden>
            <div class="aw-ai__picker-heading">
              <span><strong data-aw-ai-picker-title></strong><small data-aw-ai-picker-detail></small></span>
              <button type="button" data-aw-ai-hide-products></button>
            </div>
            <div class="aw-ai__product-grid" data-aw-ai-product-grid></div>
          </section>
          <div class="aw-ai__messages" data-aw-ai-messages aria-live="polite" aria-relevant="additions"></div>
        </div>
        <div class="aw-ai__status" data-aw-ai-status role="status" aria-live="polite"></div>
        <form class="aw-ai__composer" data-aw-ai-form novalidate>
          <div class="aw-ai__attachment" data-aw-ai-attachment hidden>
            <img data-aw-ai-attachment-preview alt="Pasted product image preview">
            <span><strong>Product image added</strong><small>Stays in this tab. It is not uploaded or auto-recognized.</small></span>
            <button type="button" data-aw-ai-remove-image>Remove</button>
          </div>
          <label class="aw-ai__visually-hidden" data-aw-ai-input-label>Ask a product question</label>
          <div class="aw-ai__input-wrap">
            <textarea data-aw-ai-input rows="1" maxlength="2000" enterkeyhint="send" autocomplete="off" autocapitalize="sentences" spellcheck="true" placeholder="Ask about a product..."></textarea>
            <div class="aw-ai__composer-tools">
              <button class="aw-ai__attach" type="button" data-aw-ai-add-image aria-label="Add product image">Product photo</button>
              <input class="aw-ai__visually-hidden" type="file" data-aw-ai-file-input accept="image/png,image/jpeg,image/webp" tabindex="-1">
              <button class="aw-ai__send" type="submit" data-aw-ai-send aria-label="Send question" title="Send question" disabled>&#8593;</button>
            </div>
          </div>
          <div class="aw-ai__composer-meta">
            <span>Chat not saved. Do not share personal or order details. Images stay in this tab.</span>
            <span><span data-aw-ai-count>0</span>/2000</span>
          </div>
        </form>
      </section>`;
    root.appendChild(template.content.cloneNode(true));

    const instanceId = randomHex(6);
    const panel = root.querySelector("[data-aw-ai-panel]");
    const title = root.querySelector("[data-aw-ai-title]");
    const subtitle = root.querySelector("[data-aw-ai-subtitle]");
    const input = root.querySelector("[data-aw-ai-input]");
    const label = root.querySelector("[data-aw-ai-input-label]");
    panel.id = `AnywellAiPanel-${instanceId}`;
    title.id = `AnywellAiTitle-${instanceId}`;
    subtitle.id = `AnywellAiSubtitle-${instanceId}`;
    input.id = `AnywellAiInput-${instanceId}`;
    label.htmlFor = input.id;
    panel.setAttribute("aria-labelledby", title.id);
    panel.setAttribute("aria-describedby", subtitle.id);
    root.querySelector("[data-aw-ai-open]").setAttribute("aria-controls", panel.id);

    const assetBase =
      safeSameOriginEndpoint(root.dataset.assetsBase || "/component/assets/") ||
      new URL("/component/assets/", window.location.origin).href;
    root.querySelector("[data-aw-ai-support-avatar]").src =
      root.dataset.supportAvatar || new URL("anywell-support-avatar.jpg", assetBase).href;
    const configuredImages = {
      x024: root.dataset.imageX024,
      ya125: root.dataset.imageYa125,
      ys1215Plus: root.dataset.imageYs1215Plus,
      ys161Max: root.dataset.imageYs161Max,
    };
    const productGrid = root.querySelector("[data-aw-ai-product-grid]");
    PRODUCTS.forEach((product) => {
      const choice = element("button", "");
      choice.type = "button";
      choice.setAttribute("data-aw-ai-product-choice", "");
      choice.dataset.productKey = product.key;
      choice.dataset.productTitle = product.title;
      choice.dataset.productImage =
        configuredImages[product.imageData] || new URL(product.image, assetBase).href;
      const image = element("img", "");
      image.src = choice.dataset.productImage;
      image.alt = product.title;
      choice.append(image, element("strong", "", product.title));
      productGrid.appendChild(choice);
    });
  }

  function init(root) {
    if (!root || root.dataset.awAiInitialized === "true") return;
    buildShell(root);
    root.dataset.awAiInitialized = "true";

    const panel = root.querySelector("[data-aw-ai-panel]");
    const titleNode =
      document.getElementById(panel.getAttribute("aria-labelledby")) ||
      root.querySelector(".aw-ai__heading strong");
    const backdrop = root.querySelector(".aw-ai__backdrop");
    const openButton = root.querySelector("[data-aw-ai-open]");
    const closeButtons = root.querySelectorAll("[data-aw-ai-close]");
    const minimizeButton = root.querySelector("[data-aw-ai-minimize]");
    const conversation = root.querySelector("[data-aw-ai-conversation]");
    const messages = root.querySelector("[data-aw-ai-messages]");
    const suggestions = root.querySelector("[data-aw-ai-suggestions]");
    const faqEntry = root.querySelector(".aw-ai__faq-entry");
    const form = root.querySelector("[data-aw-ai-form]");
    const input = root.querySelector("[data-aw-ai-input]");
    const sendButton = root.querySelector("[data-aw-ai-send]");
    const count = root.querySelector("[data-aw-ai-count]");
    const status = root.querySelector("[data-aw-ai-status]");
    const connectionLabel = root.querySelector("[data-aw-ai-connection-label]");
    const productPicker = root.querySelector("[data-aw-ai-product-picker]");
    const pickerTitle = root.querySelector("[data-aw-ai-picker-title]");
    const pickerDetail = root.querySelector("[data-aw-ai-picker-detail]");
    const productChoices = root.querySelectorAll("[data-aw-ai-product-choice]");
    const showProductButtons = root.querySelectorAll("[data-aw-ai-show-products]");
    const hideProductButtons = root.querySelectorAll("[data-aw-ai-hide-products]");
    const currentProduct = root.querySelector("[data-aw-ai-current-product]");
    const currentProductTitle = root.querySelector("[data-aw-ai-current-product-title]");
    const currentProductImage = root.querySelector("[data-aw-ai-current-product-image]");
    const addImageButton = root.querySelector("[data-aw-ai-add-image]");
    const fileInput = root.querySelector("[data-aw-ai-file-input]");
    const attachment = root.querySelector("[data-aw-ai-attachment]");
    const attachmentPreview = root.querySelector("[data-aw-ai-attachment-preview]");
    const removeImageButton = root.querySelector("[data-aw-ai-remove-image]");
    const lazySupportImages = root.querySelectorAll("img[data-aw-ai-lazy-src]");
    const assistantName = !root.dataset.assistantName || root.dataset.assistantName === "Anywell Product Assistant"
      ? "Anywell AI Customer Service"
      : root.dataset.assistantName;
    const supportUrl = safeSupportUrl(root.dataset.supportUrl || "https://anywellshop.com/pages/contact");
    const endpoint = safeServiceEndpoint(
      root.dataset.endpoint || "/api/customer-service/chat",
    );
    const recordingEndpoint = safeRecordingEndpoint(root.dataset.recordingEndpoint, endpoint);
    const state = {
      sessionRef: "sess_" + randomHex(),
      controller: null,
      lastFocused: null,
      lastQuestion: null,
      closeTimer: null,
      purchaseSyncFrame: null,
      busy: false,
      pendingSubmission: null,
      connectionVerified: false,
      timedOut: false,
      timeoutId: null,
      reading: null,
      attachmentObjectUrl: null,
      imageNeedsProductConfirmation: false,
      pickerLocale: null,
      recording: null,
      recordingControls: null,
      recordingInitialized: false,
      recordingLocale: localeForMessage("", root),
      recordingNotice: "recordingOff",
      pendingDisplays: [],
      recentHistory: [],
      guidanceContext: { need_ids: [], answer_ids: [] },
      conversationContext: {
        last_product_key: null,
        last_topic_id: "none",
        pending_question_id: null,
        pending_support_topic_id: null,
        repeat_count: 0,
        turn_index: 0,
      },
    };
    if (recordingEndpoint) {
      state.recording = createRecordingClient(recordingEndpoint, (notice) => {
        state.recordingNotice = notice;
        syncRecordingControls();
      });
    }
    if (root.dataset.productKey && currentProduct) {
      currentProduct.dataset.awAiProductKey = root.dataset.productKey;
      currentProduct.dataset.awAiVariantKey = root.dataset.variantKey || "";
      currentProduct.dataset.awAiProductTitle = root.dataset.productTitle || root.dataset.productKey;
      currentProduct.dataset.awAiProductImage = root.dataset.productImage || "";
      currentProduct.hidden = false;
      currentProductTitle.textContent = root.dataset.productTitle || root.dataset.productKey;
      if (root.dataset.productImage) {
        currentProductImage.src = root.dataset.productImage;
        currentProductImage.alt = root.dataset.productTitle || root.dataset.productKey;
        currentProductImage.hidden = false;
      }
    }

    function currentAdapter() {
      const adapter = window.AnywellAssistantAdapter;
      return adapter && typeof adapter.send === "function" ? adapter : null;
    }

    function setConnection(kind, label) {
      root.dataset.connection = kind;
      connectionLabel.textContent = label;
    }

    function refreshConnection() {
      const adapter = currentAdapter();
      const copy = copyForLocale(localeForMessage("", root));
      if (!navigator.onLine) {
        setConnection("offline", copy.offlineConnection);
      } else if (state.connectionVerified) {
        setConnection("connected", copy.verifiedConnection);
      } else if (adapter || endpoint) {
        setConnection("pending", copy.serviceReadyConnection);
      } else {
        setConnection("not-configured", copy.notConfiguredConnection);
      }
    }

    function mountRecordingControls() {
      if (!recordingEndpoint) return;
      const copy = copyForLocale(state.recordingLocale);
      const details = element("details", "aw-ai__recording");
      const summary = element("summary", "", copy.recordingSummary);
      const notice = element("p", "aw-ai__recording-status", copy.recordingOff);
      notice.setAttribute("role", "status");
      const explanation = element("p", "", copy.recordingNotice);
      const privacy = element("p", "", copy.recordingPrivacy);
      const lifetime = element("p", "", copy.recordingLifetime);
      const actions = element("div", "aw-ai__actions");
      const resume = element("button", "aw-ai__action", copy.recordingResume);
      const stop = element("button", "aw-ai__action", copy.recordingStop);
      const remove = element("button", "aw-ai__action", copy.recordingDelete);
      resume.type = stop.type = remove.type = "button";
      actions.append(resume, stop, remove);
      const confirmation = element("div", "aw-ai__recording-confirmation");
      confirmation.hidden = true;
      const prompt = element("p", "", copy.recordingDeletePrompt);
      const confirm = element("button", "aw-ai__action", copy.recordingDeleteConfirm);
      const keep = element("button", "aw-ai__action", copy.recordingKeep);
      confirm.type = keep.type = "button";
      confirmation.append(prompt, confirm, keep);
      details.append(summary, explanation, privacy, lifetime, actions, confirmation);
      const meta = form.querySelector(".aw-ai__composer-meta");
      form.insertBefore(details, meta);
      form.insertBefore(notice, meta);
      state.recordingControls = { details, summary, notice, explanation, privacy, lifetime, resume, stop, remove, confirmation, prompt, confirm, keep };
      resume.addEventListener("click", async () => {
        state.recordingInitialized = true;
        const enabled = await state.recording.start(state.recordingLocale, window.matchMedia("(max-width: 699px)").matches ? "mobile" : "desktop");
        if (enabled) state.recentHistory = [];
        syncRecordingControls();
      });
      stop.addEventListener("click", () => {
        state.recordingInitialized = true;
        state.recording.stop();
      });
      remove.addEventListener("click", () => {
        confirmation.hidden = false;
        confirm.focus();
      });
      keep.addEventListener("click", () => {
        confirmation.hidden = true;
        remove.focus();
      });
      confirm.addEventListener("click", async () => {
        confirmation.hidden = true;
        state.recordingInitialized = true;
        if (state.controller) state.controller.abort();
        await state.recording.remove();
        syncRecordingControls();
        summary.focus();
      });
      syncRecordingControls();
    }

    function syncRecordingControls() {
      const controls = state.recordingControls;
      if (!controls) return;
      const copy = copyForLocale(state.recordingLocale);
      const saving = state.recording.isSaving();
      const pending = state.recording.isPending();
      const starting = pending && state.recordingNotice === "recordingStarting";
      controls.summary.textContent = saving || starting || !state.recordingInitialized ? copy.recordingSummaryOn : copy.recordingOff;
      controls.notice.textContent = copy[state.recordingNotice];
      for (const [name, key] of Object.entries({ explanation: "recordingNotice", privacy: "recordingPrivacy", lifetime: "recordingLifetime", resume: "recordingResume", stop: "recordingStop", remove: "recordingDelete", prompt: "recordingDeletePrompt", confirm: "recordingDeleteConfirm", keep: "recordingKeep" })) controls[name].textContent = copy[key];
      controls.notice.hidden = !state.recordingInitialized || ["recordingOff", "recordingStarting", "recordingSaving", "recordingStopped"].includes(state.recordingNotice);
      controls.resume.hidden = saving || !state.recordingInitialized;
      controls.resume.disabled = pending || state.busy;
      controls.stop.hidden = !saving && state.recordingInitialized && !pending;
      controls.remove.hidden = !state.recording.hasSaved();
      controls.remove.disabled = pending;
      controls.confirm.disabled = pending;
      const meta = form.querySelector(".aw-ai__composer-meta > span:first-child");
      if (meta) meta.textContent = state.recordingLocale === "zh-CN" ? "请勿发送个人、付款或健康信息。" : "Please keep personal, payment and health details out of chat.";
    }

    function queueDisplayed(wrapper, receipt, startedAt, holdMs) {
      if (!receipt) return;
      state.pendingDisplays.push({ wrapper, receipt, startedAt, holdMs });
      window.requestAnimationFrame(flushDisplayed);
    }

    function flushDisplayed() {
      if (document.hidden || panel.hidden || root.dataset.open !== "true") return;
      const pending = state.pendingDisplays.splice(0);
      for (const item of pending) {
        if (!item.wrapper.isConnected) continue;
        const now = performance.now();
        state.recording.event(item.receipt, "displayed", now - item.startedAt, item.holdMs);
      }
    }

    function updateComposer() {
      const length = input.value.length;
      count.textContent = String(length);
      sendButton.disabled =
        state.busy || state.imageNeedsProductConfirmation || input.value.trim().length === 0;
      [currentProduct, addImageButton, fileInput, removeImageButton, ...showProductButtons,
        ...productChoices, ...root.querySelectorAll("[data-aw-ai-context-control]")].forEach((control) => {
        if (control) control.disabled = state.busy;
      });
      input.style.height = "auto";
      input.style.height = Math.min(input.scrollHeight, 112) + "px";
      syncRecordingControls();
    }

    function showProductPicker(locale) {
      if (state.busy) return;
      if (!productPicker) return;
      const pickerLocale = locale ? normalizeLocale(locale) : localeForMessage(input.value, root);
      const copy = copyForLocale(pickerLocale);
      state.pickerLocale = pickerLocale;
      productPicker.setAttribute("aria-label", copy.pickerAria);
      if (pickerTitle) pickerTitle.textContent = copy.pickerTitle;
      if (pickerDetail) pickerDetail.textContent = copy.pickerDetail;
      hideProductButtons.forEach((button) => {
        button.textContent = copy.pickerClose;
        button.setAttribute("aria-label", copy.pickerCloseAria);
      });
      productPicker.hidden = false;
      productChoices.forEach((choice) => {
        choice.setAttribute(
          "aria-pressed",
          String(choice.dataset.productKey === (root.dataset.productKey || "")),
        );
      });
      scrollToLatest();
    }

    function hideProductPicker() {
      if (productPicker) productPicker.hidden = true;
      state.pickerLocale = null;
    }

    function selectProduct(source) {
      if (state.busy) return;
      const productKey = source.dataset.productKey || source.dataset.awAiProductKey || "";
      const variantKey = source.dataset.variantKey || source.dataset.awAiVariantKey || "";
      if (!productKey) return;
      const selectionLocale = state.pickerLocale || localeForMessage(input.value, root);
      root.dataset.productKey = productKey;
      root.dataset.variantKey = variantKey;
      root.dataset.productTitle = source.dataset.productTitle || source.dataset.awAiProductTitle || "";
      root.dataset.productImage = source.dataset.productImage || source.dataset.awAiProductImage || "";
      if (currentProduct) {
        currentProduct.dataset.awAiProductKey = productKey;
        currentProduct.dataset.awAiVariantKey = variantKey;
        currentProduct.dataset.awAiProductTitle = root.dataset.productTitle;
        currentProduct.dataset.awAiProductImage = root.dataset.productImage;
        currentProduct.hidden = false;
      }
      if (currentProductTitle) {
        currentProductTitle.textContent = root.dataset.productTitle || productKey;
      }
      if (currentProductImage) {
        if (root.dataset.productImage) {
          currentProductImage.src = root.dataset.productImage;
          currentProductImage.alt = root.dataset.productTitle || productKey;
          currentProductImage.hidden = false;
        } else {
          currentProductImage.removeAttribute("src");
          currentProductImage.alt = "";
          currentProductImage.hidden = true;
        }
      }
      state.conversationContext = {
        last_product_key: null,
        last_topic_id: "none",
        pending_question_id: null,
        pending_support_topic_id: null,
        repeat_count: 0,
        turn_index: state.conversationContext.turn_index,
      };
      state.recentHistory = [];
      clearAttachment();
      productChoices.forEach((choice) => {
        choice.setAttribute("aria-pressed", String(choice.dataset.productKey === productKey));
      });
      hideProductPicker();
      const copy = copyForLocale(selectionLocale);
      status.textContent = copy.productConfirmed.replace(
        "{product}",
        root.dataset.productTitle || productKey,
      );
      input.focus();
    }

    function clearAttachment() {
      if (state.attachmentObjectUrl) URL.revokeObjectURL(state.attachmentObjectUrl);
      state.attachmentObjectUrl = null;
      state.imageNeedsProductConfirmation = false;
      if (attachmentPreview) attachmentPreview.removeAttribute("src");
      if (attachment) attachment.hidden = true;
      if (fileInput) fileInput.value = "";
      updateComposer();
    }

    function attachProductImage(file) {
      if (state.busy) return;
      const copy = copyForLocale(localeForMessage(input.value, root));
      if (!file || !/^image\/(?:png|jpeg|webp)$/.test(file.type)) {
        status.textContent = copy.imageFormat;
        return;
      }
      if (file.size > 6 * 1024 * 1024) {
        status.textContent = copy.imageTooLarge;
        return;
      }
      clearAttachment();
      state.attachmentObjectUrl = URL.createObjectURL(file);
      state.imageNeedsProductConfirmation = true;
      attachmentPreview.src = state.attachmentObjectUrl;
      attachment.hidden = false;
      status.textContent = copy.imagePrivacy;
      showProductPicker();
      updateComposer();
    }

    function vagueProductReference(text) {
      return (
        !root.dataset.productKey &&
        (/\b(?:this|that|it|this (?:chair|scooter|car|vehicle))\b/i.test(text) ||
          /这个|这台|它|这辆|这个车/.test(text))
      );
    }

    function userIsNearBottom() {
      return conversation.scrollHeight - conversation.scrollTop - conversation.clientHeight < 96;
    }

    function scrollToLatest(force = false) {
      if (!force && !userIsNearBottom()) return;
      window.requestAnimationFrame(() => {
        conversation.scrollTop = conversation.scrollHeight;
      });
    }

    function loadSupportImages() {
      lazySupportImages.forEach((image) => {
        if (!image.getAttribute("src") && image.dataset.awAiLazySrc) {
          image.src = image.dataset.awAiLazySrc;
        }
      });
    }

    function appendMessage(role, text, outcome = "") {
      const shouldStick = userIsNearBottom();
      const className =
        "aw-ai__message aw-ai__message--" +
        role +
        (outcome ? " aw-ai__message--" + outcome : "");
      const wrapper = element("div", className);
      wrapper.appendChild(
        element(
          "span",
          "aw-ai__message-label",
          role === "user" ? (containsCjk(text) ? "你" : "You") : assistantName,
        ),
      );
      const bubble = element("div", "aw-ai__bubble");
      if (role === "assistant") {
        for (const paragraph of answerParagraphs(text)) {
          const paragraphNode = element("p", "");
          appendHighlightedText(paragraphNode, paragraph);
          bubble.appendChild(paragraphNode);
        }
      } else {
        bubble.appendChild(element("p", "", text));
      }
      wrapper.appendChild(bubble);
      messages.appendChild(wrapper);
      scrollToLatest(shouldStick);
      return wrapper;
    }

    function appendSource(wrapper, response, locale) {
      const citations = Array.isArray(response.citations) ? response.citations : [];
      if (!citations.length) return;

      const stable = citations.every((citation) => citation.freshness === "verified_stable");
      const fresh = citations.every((citation) => citation.freshness === "fresh_live");
      const copy = copyForLocale(locale);
      const label = stable
        ? copy.verifiedStableSource
        : fresh
          ? copy.verifiedFreshSource
          : copy.unavailableSource;
      const source = element(
        "div",
        "aw-ai__source " + (stable || fresh ? "aw-ai__source--verified" : "aw-ai__source--unavailable"),
        label,
      );
      wrapper.appendChild(source);
    }

    function appendFeedback(wrapper, _response, locale, recordingReceipt) {
      const copy = copyForLocale(locale);
      const feedback = element("div", "aw-ai__actions");
      feedback.setAttribute("aria-label", copy.feedbackAria);
      const label = element("span", "aw-ai__source", copy.feedbackPrompt);
      const yes = element("button", "aw-ai__action", copy.feedbackYes);
      const no = element("button", "aw-ai__action", copy.feedbackNo);
      yes.type = "button";
      no.type = "button";

      [yes, no].forEach((button) => {
        button.addEventListener("click", async () => {
          if (recordingReceipt && state.recording && state.recording.canFeedback(recordingReceipt)) {
            yes.disabled = no.disabled = true;
            label.textContent = copy.feedbackSaving;
            try {
              await state.recording.feedback(recordingReceipt, button === yes);
              feedback.replaceChildren(element("span", "aw-ai__source", copy.feedbackSaved));
            } catch (_error) {
              label.textContent = copy.feedbackFailed;
              yes.disabled = no.disabled = false;
            }
            return;
          }
          feedback.replaceChildren(
            element("span", "aw-ai__source", copy.feedbackThanks),
          );
        });
      });

      feedback.append(label, yes, no);
      wrapper.appendChild(feedback);
    }

    function stopReading() {
      if (!state.reading) return;
      const reading = state.reading;
      state.reading = null;
      window.speechSynthesis.cancel();
      reading.button.setAttribute("aria-pressed", "false");
      reading.button.setAttribute("aria-label", reading.label);
      reading.button.title = reading.label;
    }

    function updateReadingControls() {
      root.querySelectorAll("[data-aw-ai-read]").forEach((button) => {
        const available = window.speechSynthesis && window.SpeechSynthesisUtterance
          && localReadingVoice(window.speechSynthesis.getVoices(), button.dataset.awAiRead);
        button.setAttribute("aria-disabled", String(!available));
        if (!available) button.title = copyForLocale(button.dataset.awAiRead).localVoiceUnavailable;
        else if (button.getAttribute("aria-pressed") !== "true") button.title = copyForLocale(button.dataset.awAiRead).readAloud;
      });
    }

    function appendReplyTools(wrapper, response, locale) {
      const copy = copyForLocale(locale);
      const row = element("div", "aw-ai__reply-tools");
      row.setAttribute("role", "group");
      row.setAttribute("aria-label", copy.replyTools);
      const notice = element("span", "aw-ai__tool-notice");
      notice.setAttribute("role", "status");
      notice.setAttribute("aria-live", "polite");
      const makeTool = (name, label) => {
        const button = element("button", "aw-ai__reply-tool");
        button.type = "button";
        button.title = label;
        button.setAttribute("aria-label", label);
        button.appendChild(replyToolIcon(name));
        row.appendChild(button);
        return button;
      };
      const read = makeTool("read", copy.readAloud);
      read.dataset.awAiRead = locale;
      read.setAttribute("aria-pressed", "false");
      read.addEventListener("click", () => {
        if (state.reading && state.reading.button === read) { stopReading(); return; }
        stopReading();
        const voice = window.speechSynthesis && window.SpeechSynthesisUtterance
          && localReadingVoice(window.speechSynthesis.getVoices(), locale);
        if (!voice) { notice.textContent = copy.localVoiceUnavailable; return; }
        const utterance = new window.SpeechSynthesisUtterance(repeatAnswerText(response.answer_text));
        notice.textContent = "";
        utterance.voice = voice;
        utterance.lang = voice.lang;
        utterance.rate = 0.9;
        state.reading = { button: read, label: copy.readAloud, utterance };
        read.setAttribute("aria-pressed", "true");
        read.setAttribute("aria-label", copy.stopReading);
        read.title = copy.stopReading;
        const finish = () => { if (state.reading && state.reading.utterance === utterance) stopReading(); };
        utterance.onend = finish;
        utterance.onerror = () => {
          if (!state.reading || state.reading.utterance !== utterance) return;
          finish();
          notice.textContent = copy.localVoiceUnavailable;
        };
        try { window.speechSynthesis.speak(utterance); }
        catch (_error) { finish(); notice.textContent = copy.localVoiceUnavailable; }
      });
      const copyButton = makeTool("copy", copy.copyAnswer);
      copyButton.addEventListener("click", async () => {
        if (copyButton.disabled) return;
        copyButton.disabled = true;
        try {
          await navigator.clipboard.writeText(replyShareText(response));
          notice.textContent = copy.copiedAnswer;
        } catch (_error) { notice.textContent = copy.copyUnavailable; }
        finally { copyButton.disabled = false; }
      });
      if (isSubstantiveReply(response)) {
        const repeat = makeTool("repeat", copy.repeatAnswer);
        const repeated = element("div", "aw-ai__repeated-answer");
        repeated.hidden = true;
        repeated.tabIndex = -1;
        repeated.setAttribute("role", "region");
        repeated.setAttribute("aria-label", copy.repeatedAnswer);
        for (const paragraph of answerParagraphs(repeatAnswerText(response.answer_text))) {
          const node = element("p", "");
          appendHighlightedText(node, paragraph);
          repeated.appendChild(node);
        }
        repeat.setAttribute("aria-expanded", "false");
        repeat.addEventListener("click", () => {
          repeated.hidden = !repeated.hidden;
          repeat.setAttribute("aria-expanded", String(!repeated.hidden));
          if (!repeated.hidden) repeated.focus();
        });
        wrapper.appendChild(repeated);
      }
      wrapper.append(row, notice);
      updateReadingControls();
    }

    function appendActions(wrapper, response, context = {}) {
      const actions = Array.isArray(response.ui_actions) ? response.ui_actions : [];
      const actionRow = element("div", "aw-ai__actions");
      const locale = context.locale || localeForMessage(response.answer_text, root);
      const copy = copyForLocale(locale);
      const retryQuestion = context.question || state.lastQuestion;

      actions.forEach((item) => {
        if (!item || typeof item !== "object") return;

        if (item.action === "open_product") {
          const target = safePublicTarget(item.public_target);
          if (!target) return;
          const link = element("a", "aw-ai__action", item.label || copy.openProduct);
          link.href = target;
          actionRow.appendChild(link);
        }

        if (item.action === "choose_product") {
          const choose = element("button", "aw-ai__action", item.label || copy.chooseProductAction);
          choose.type = "button";
          choose.dataset.awAiContextControl = "";
          choose.addEventListener("click", () => showProductPicker(locale));
          actionRow.appendChild(choose);
        }

        if (item.action === "retry") {
          const retry = element("button", "aw-ai__action", item.label || copy.retry);
          retry.type = "button";
          if (item.retry_after_seconds != null) {
            const retryAfterSeconds = boundedRetryAfterSeconds(item.retry_after_seconds);
            retry.disabled = true;
            retry.setAttribute("aria-disabled", "true");
            window.setTimeout(() => {
              if (!retry.isConnected) return;
              retry.disabled = false;
              retry.setAttribute("aria-disabled", "false");
            }, retryAfterSeconds * 1000 + RETRY_TIMER_BUFFER_MS);
          }
          retry.addEventListener("click", () => {
            if (!retry.disabled && retryQuestion) {
              submitText(retryQuestion, {
                replaceTarget: wrapper,
                suppressUser: true,
                locale,
              });
            }
          });
          actionRow.appendChild(retry);
        }

        if (item.action === "edit_message") {
          const edit = element("button", "aw-ai__action", item.label || copy.editQuestion);
          edit.type = "button";
          edit.addEventListener("click", () => input.focus());
          actionRow.appendChild(edit);
        }
      });

      if (actionRow.childElementCount) wrapper.appendChild(actionRow);
    }

    function appendChoices(wrapper, response, locale) {
      const copy = copyForLocale(locale);
      const choices = element("div", "aw-ai__choices");
      const guidedOptions =
        response.guidance && Array.isArray(response.guidance.next_options)
          ? response.guidance.next_options
          : [];
      if (guidedOptions.length) {
        guidedOptions.forEach((option) => {
          if (!option || typeof option.message !== "string" || typeof option.label !== "string") return;
          const button = element("button", "aw-ai__chip", option.label);
          button.type = "button";
          button.dataset.awAiContextControl = "";
          button.addEventListener("click", () => submitText(option.message));
          choices.appendChild(button);
        });
        if (choices.childElementCount) wrapper.appendChild(choices);
        return;
      }
      const isYs16 =
        response.product_context.product_key === "ys16-1-max" ||
        /12Ah or 20Ah/i.test(response.answer_text);

      if (!isYs16) {
        showProductPicker(locale);
        return;
      }

      const options = [
            {
              label: "12Ah",
              product: "ys16-1-max",
              variant: "ys16-1-max-12ah",
              question: copy.range12Question,
            },
            {
              label: "20Ah",
              product: "ys16-1-max",
              variant: "ys16-1-max-20ah",
              question: copy.range20Question,
            },
            {
              label: copy.compareBoth,
              product: "ys16-1-max",
              variant: "",
              question: copy.compareBothQuestion,
            },
          ];

      options.forEach((option) => {
        const button = element("button", "aw-ai__chip", option.label);
        button.type = "button";
        button.dataset.awAiContextControl = "";
        button.addEventListener("click", () => {
          if (state.busy) return;
          root.dataset.productKey = option.product;
          root.dataset.variantKey = option.variant;
          submitText(option.question);
        });
        choices.appendChild(button);
      });

      wrapper.appendChild(choices);
    }

    function appendHandoff(wrapper, response, locale) {
      const copy = copyForLocale(locale);
      const preview = element("div", "aw-ai__handoff-preview");
      preview.appendChild(element("strong", "", copy.handoffHeading));
      preview.appendChild(
        element(
          "span",
          "",
          response.handoff && response.handoff.redacted_summary
            ? response.handoff.redacted_summary
            : copy.handoffDefaultSummary,
        ),
      );

      const consentLabel = element("label", "aw-ai__consent");
      const consent = document.createElement("input");
      consent.type = "checkbox";
      consent.setAttribute("aria-label", copy.handoffConsentAria);
      consentLabel.append(
        consent,
        element("span", "", copy.handoffConsent),
      );
      preview.appendChild(consentLabel);

      let supportAction;
      if (supportUrl) {
        supportAction = element(
          "a",
          "aw-ai__action aw-ai__action--primary",
          copy.openSecureSupport,
        );
        supportAction.href = supportUrl;
        supportAction.target = "_blank";
        supportAction.rel = "noopener noreferrer";
        supportAction.setAttribute("aria-disabled", "true");
        supportAction.tabIndex = -1;
        supportAction.addEventListener("click", (event) => {
          if (!consent.checked) event.preventDefault();
        });
        consent.addEventListener("change", () => {
          supportAction.setAttribute("aria-disabled", String(!consent.checked));
          supportAction.tabIndex = consent.checked ? 0 : -1;
        });
      } else {
        supportAction = element("button", "aw-ai__action", copy.secureSupportNotConnected);
        supportAction.type = "button";
        supportAction.disabled = true;
        supportAction.setAttribute("aria-disabled", "true");
        consent.disabled = true;
      }
      preview.appendChild(supportAction);
      wrapper.appendChild(preview);
    }

    function renderResponse(response, context = {}) {
      const shouldStick = userIsNearBottom();
      const locale = context.locale || localeForMessage(response.answer_text, root);
      const replaceTarget = context.replaceTarget || null;
      if (replaceTarget && replaceTarget.isConnected) replaceTarget.remove();
      const className =
        response.outcome === "privacy_intercept"
          ? "privacy"
          : response.outcome === "handoff"
            ? "handoff"
            : response.outcome === "unavailable"
              ? "unavailable"
              : "";
      const wrapper = appendMessage("assistant", response.answer_text, className);
      appendSource(wrapper, response, locale);

      if (response.guidance.next_options.length) appendChoices(wrapper, response, locale);
      if (response.handoff || response.outcome === "handoff" || response.outcome === "privacy_intercept") {
        appendHandoff(wrapper, response, locale);
      }
      appendActions(wrapper, response, { ...context, locale });
      if (isSubstantiveReply(response) || response.outcome === "clarify") appendReplyTools(wrapper, response, locale);
      if (isSubstantiveReply(response)) appendFeedback(wrapper, response, locale, context.recordingReceipt);
      if (shouldStick) {
        window.requestAnimationFrame(() => {
          conversation.scrollTop += wrapper.getBoundingClientRect().top - conversation.getBoundingClientRect().top - 8;
        });
      }
      return wrapper;
    }

    function localResponse(outcome, answer, reasonCode, summary, locale) {
      const copy = copyForLocale(locale);
      return {
        schema_version: 1,
        request_id: "req_" + randomHex(),
        outcome,
        answer_text: answer,
        product_context: {
          product_key: root.dataset.productKey || null,
          variant_key: root.dataset.variantKey || null,
        },
        citations: [],
        fact_freshness: {
          dynamic_query_required: false,
          dynamic_query_succeeded: false,
          checked_at: null,
          stale_or_unavailable_sources: [],
        },
        handoff: reasonCode
          ? {
              reason_code: reasonCode,
              redacted_summary: summary,
              consent_required: true,
            }
          : null,
        ui_actions: [{ action: "edit_message", label: copy.editQuestion, public_target: null }],
        guidance_context: state.guidanceContext,
        conversation_context: state.conversationContext,
        guidance: {
          stage: reasonCode ? "boundary" : "idle",
          next_question_id: null,
          next_options: [],
          recommendations: [],
        },
        privacy: {
          input_redacted_before_core: true,
          raw_input_persisted: false,
          customer_identity_persisted: false,
        },
        generation: {
          mode: "deterministic",
          provider: "none",
          model: null,
          reason_code: "NOT_CONFIGURED",
          stored: false,
        },
      };
    }

    function renderPrivacy(piiTypes, locale) {
      const copy = copyForLocale(locale);
      appendMessage("user", copy.withheld);
      const code = piiTypes.includes("order_reference") ? "ORDER_OR_ACCOUNT" : "PRIVACY_INTERCEPT";
      renderResponse(
        localResponse(
          "privacy_intercept",
          copy.privacy,
          code,
          copy.privacySummary,
          locale,
        ),
        { locale },
      );
    }

    function renderRestricted(restriction, locale) {
      const copy = copyForLocale(locale);
      appendMessage("user", copy.withheld);
      renderResponse(
        localResponse("handoff", restriction.answer, restriction.code, restriction.summary, locale),
        { locale },
      );
    }

    function setBusy(label, locale) {
      stopReading();
      const copy = copyForLocale(locale);
      state.busy = true;
      root.dataset.busy = "true";
      messages.setAttribute("aria-busy", "true");
      updateComposer();
      status.replaceChildren();
      const row = element("div", "aw-ai__status-row");
      const statusCopy = element("span", "aw-ai__status-copy");
      const dots = element("span", "aw-ai__typing");
      dots.setAttribute("aria-hidden", "true");
      dots.append(element("span"), element("span"), element("span"));
      statusCopy.append(dots, element("span", "", label));
      const cancel = element("button", "aw-ai__cancel", copy.cancel);
      cancel.type = "button";
      cancel.addEventListener("click", () => {
        if (state.controller) state.controller.abort();
      });
      row.append(statusCopy, cancel);
      status.appendChild(row);
    }

    function clearBusy() {
      state.busy = false;
      root.dataset.busy = "false";
      messages.setAttribute("aria-busy", "false");
      state.controller = null;
      status.replaceChildren();
      updateComposer();
    }

    function selectedShopifyVariantId() {
      const value = new URL(window.location.href).searchParams.get("variant") || root.dataset.variantId;
      return /^[0-9]{1,20}$/.test(value || "") ? value : null;
    }

    function buildRequest(messageRedacted, locale) {
      return {
        schema_version: 1,
        request_id: "req_" + randomHex(),
        session_ref: state.sessionRef,
        locale: normalizeLocale(locale || localeForMessage(messageRedacted, root)),
        message_redacted: messageRedacted,
        pii_types: [],
        product_context: {
          product_key: root.dataset.productKey || null,
          variant_key: root.dataset.variantKey || null,
          page_handle: root.dataset.pageHandle || null,
          shopify_variant_id: selectedShopifyVariantId(),
        },
        source_policy_id: SOURCE_POLICY_ID,
        recent_history: state.recentHistory.slice(-MAX_HISTORY_ITEMS).map((item) => ({
          role: item.role,
          text: item.text.slice(0, 600),
        })),
        guidance_context: state.guidanceContext,
        conversation_context: state.conversationContext,
        client_context: {
          device_class: window.matchMedia("(max-width: 699px)").matches ? "mobile" : "desktop",
          network_state: navigator.onLine ? "online" : "offline",
        },
      };
    }

    async function sendRequest(request, signal) {
      if (state.recording && state.recording.isSaving()) return state.recording.chat(request, signal);
      const adapter = currentAdapter();
      if (adapter) return adapter.send(request, { signal });
      if (!endpoint) throw new Error("NOT_CONFIGURED");

      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        referrerPolicy: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
        signal,
      });
      if (!response.ok) {
        if (response.status === 429) {
          const rateLimited = new Error("RATE_LIMITED");
          rateLimited.code = "RATE_LIMITED";
          rateLimited.status = response.status;
          rateLimited.retryAfterSeconds = boundedRetryAfterSeconds(
            response.headers.get("Retry-After"),
          );
          throw rateLimited;
        }
        throw new Error("REQUEST_FAILED");
      }
      const contentType = String(response.headers.get("Content-Type") || "").toLowerCase();
      if (!contentType.startsWith("application/json")) throw new Error("INVALID_CONTENT_TYPE");
      const announcedSize = Number(response.headers.get("Content-Length") || 0);
      if (announcedSize > MAX_RESPONSE_BYTES) throw new Error("RESPONSE_TOO_LARGE");
      const body = await response.text();
      if (new TextEncoder().encode(body).byteLength > MAX_RESPONSE_BYTES) {
        throw new Error("RESPONSE_TOO_LARGE");
      }
      try {
        return JSON.parse(body);
      } catch (_error) {
        throw new Error("INVALID_RESPONSE");
      }
    }

    async function submitText(value, options = {}) {
      if (state.busy) return;
      state.pendingSubmission = {
        replaceTarget: options.replaceTarget || null,
        suppressUser: options.suppressUser === true,
        locale: options.locale || localeForMessage(value, root),
      };
      input.value = value;
      updateComposer();
      form.requestSubmit();
    }

    async function handleSubmit(event) {
      event.preventDefault();
      if (state.busy) return;

      const raw = input.value.trim();
      if (!raw) return;
      stopReading();
      const submission = state.pendingSubmission || {};
      state.pendingSubmission = null;
      const responseLocale = submission.locale || localeForMessage(raw, root);
      const copy = copyForLocale(responseLocale);
      if (state.imageNeedsProductConfirmation) {
        status.textContent = copy.confirmPicturedModel;
        showProductPicker(responseLocale);
        return;
      }
      if (vagueProductReference(raw)) {
        status.textContent = copy.chooseProduct;
        showProductPicker(responseLocale);
        return;
      }

      input.value = "";
      clearAttachment();
      updateComposer();
      const screened = screenMessage(raw);
      if (screened.piiTypes.length) {
        state.lastQuestion = null;
        renderPrivacy(screened.piiTypes, responseLocale);
        return;
      }

      const restriction = restrictedIntent(screened.redacted, responseLocale);
      if (restriction) {
        state.lastQuestion = null;
        renderRestricted(restriction, responseLocale);
        return;
      }

      state.lastQuestion = screened.redacted;
      if (faqEntry) faqEntry.hidden = true;
      if (!submission.suppressUser) appendMessage("user", screened.redacted);
      const responseContext = {
        replaceTarget: submission.replaceTarget || null,
        question: screened.redacted,
        locale: responseLocale,
      };

      if (!navigator.onLine) {
        setConnection("offline", copy.offlineConnection);
        const offline = localResponse("unavailable", copy.offline, null, "", responseLocale);
        offline.ui_actions = [{ action: "retry", label: copy.retry, public_target: null }];
        renderResponse(offline, responseContext);
        return;
      }

      const request = buildRequest(screened.redacted, responseLocale);
      const startedAt = performance.now();
      const replyDeadline = performance.now() + replyDelayMilliseconds();
      let recordingAttempt = false;
      let recordingReceipt = null;
      let holdMs = 0;
      if (state.recording) {
        state.recordingLocale = responseLocale;
        syncRecordingControls();
      }
      state.controller = new AbortController();
      state.timedOut = false;
      setBusy(copy.replyPending, responseLocale);

      try {
        // Start only when a screened question is submitted, never on page view.
        if (state.recording && !state.recordingInitialized) {
          state.recordingInitialized = true;
          await state.recording.start(responseLocale, request.client_context.device_class);
        }
        if (state.controller.signal.aborted) throw new DOMException("Reply cancelled", "AbortError");
        recordingAttempt = Boolean(state.recording && state.recording.isSaving());
        state.timeoutId = window.setTimeout(() => {
          state.timedOut = true;
          if (state.controller) state.controller.abort();
        }, REQUEST_TIMEOUT_MS);
        const response = validateResponse(
          await sendRequest(request, state.controller.signal),
          request.request_id,
        );
        if (recordingAttempt) recordingReceipt = state.recording.receipt(request.request_id);
        if (state.timeoutId) window.clearTimeout(state.timeoutId);
        state.timeoutId = null;
        if (state.controller.signal.aborted) throw new DOMException("Reply cancelled", "AbortError");
        if (shouldDelayReply(response)) {
          const holdStartedAt = performance.now();
          try {
            await waitForReplyPresentation(replyDeadline, state.controller.signal);
          } finally {
            holdMs = performance.now() - holdStartedAt;
          }
        }
        if (response.product_context.product_key) {
          root.dataset.productKey = response.product_context.product_key;
        }
        if (response.product_context.variant_key) {
          root.dataset.variantKey = response.product_context.variant_key;
        }
        state.guidanceContext = {
          need_ids: [...response.guidance_context.need_ids],
          answer_ids: [...response.guidance_context.answer_ids],
        };
        state.conversationContext = {
          last_product_key: response.conversation_context.last_product_key,
          last_topic_id: response.conversation_context.last_topic_id,
          pending_question_id: response.conversation_context.pending_question_id,
          pending_support_topic_id: response.conversation_context.pending_support_topic_id,
          repeat_count: response.conversation_context.repeat_count,
          turn_index: response.conversation_context.turn_index,
        };
        state.connectionVerified = true;
        setConnection("connected", copy.verifiedConnection);
        state.recentHistory.push(
          { role: "user", text: request.message_redacted.slice(0, 600) },
          { role: "assistant", text: response.answer_text.slice(0, 600) },
        );
        state.recentHistory = state.recentHistory.slice(-MAX_HISTORY_ITEMS);
        const wrapper = renderResponse(response, { ...responseContext, recordingReceipt });
        if (recordingReceipt) queueDisplayed(wrapper, recordingReceipt, startedAt, holdMs);
      } catch (error) {
        state.connectionVerified = false;
        if (recordingReceipt && error && error.name === "AbortError") {
          state.recording.event(recordingReceipt, "cancelled", performance.now() - startedAt, holdMs);
        }
        if (error && error.name === "AbortError") {
          setConnection(
            state.timedOut ? "error" : "pending",
            state.timedOut ? copy.timedOutConnection : copy.cancelledConnection,
          );
          const interrupted = localResponse(
            "unavailable",
            recordingAttempt ? copy.recordingInterrupted : state.timedOut ? copy.timeout : copy.cancelled,
            null,
            "",
            responseLocale,
          );
          interrupted.ui_actions = [];
          renderResponse(interrupted, responseContext);
        } else if (error && error.code === "RATE_LIMITED") {
          setConnection("error", copy.rateLimitedConnection);
          const rateLimited = localResponse(
            "unavailable",
            copy.rateLimited,
            null,
            "",
            responseLocale,
          );
          rateLimited.ui_actions = recordingAttempt ? [] : [
            {
              action: "retry",
              label: copy.retry,
              public_target: null,
              retry_after_seconds: error.retryAfterSeconds,
            },
          ];
          renderResponse(rateLimited, responseContext);
        } else if (error && error.message === "NOT_CONFIGURED") {
          setConnection("not-configured", copy.notConfiguredConnection);
          const unavailable = localResponse(
            "unavailable",
            copy.dynamicUnavailable,
            null,
            "",
            responseLocale,
          );
          unavailable.ui_actions = [
            { action: "retry", label: copy.retryAfterConnection, public_target: null },
          ];
          renderResponse(unavailable, responseContext);
        } else {
          setConnection("error", copy.transportConnection);
          const failure = localResponse(
            "unavailable",
            recordingAttempt ? copy.recordingTransportFailure : copy.transportFailure,
            null,
            "",
            responseLocale,
          );
          failure.ui_actions = recordingAttempt ? [] : [{ action: "retry", label: copy.retry, public_target: null }];
          renderResponse(failure, responseContext);
        }
      } finally {
        if (state.timeoutId) window.clearTimeout(state.timeoutId);
        state.timeoutId = null;
        state.timedOut = false;
        clearBusy();
      }
    }

    function focusableElements() {
      return Array.from(
        panel.querySelectorAll(
          'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), summary:not([tabindex="-1"])',
        ),
      ).filter((node) => {
        const closedDetails = node.closest("details:not([open])");
        return !node.hidden && node.offsetParent !== null &&
          (!closedDetails || node === closedDetails.querySelector("summary"));
      });
    }

    function syncModalState() {
      const isOpen =
        root.dataset.open === "true" ||
        openButton.getAttribute("aria-expanded") === "true";
      const isMobileModal = isOpen && window.matchMedia("(max-width: 699px)").matches;
      panel.setAttribute("aria-modal", String(isMobileModal));
      document.documentElement.classList.toggle("aw-ai-lock", isMobileModal);
    }

    function isVisiblePurchaseSurface(node) {
      const style = window.getComputedStyle(node);
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        style.visibility === "collapse" ||
        Number.parseFloat(style.opacity || "1") <= 0
      ) {
        return false;
      }
      const rect = node.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.right > 0 &&
        rect.bottom > 0 &&
        rect.left < document.documentElement.clientWidth &&
        rect.top < document.documentElement.clientHeight
      );
    }

    function isBlockingPurchaseSurface(node) {
      const style = window.getComputedStyle(node);
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        style.visibility === "collapse"
      ) {
        return false;
      }
      const rect = node.getBoundingClientRect();
      const hasBox = rect.width > 0 && rect.height > 0;
      const isEntering =
        hasBox &&
        PURCHASE_SURFACE_SELECTORS.some((selector) => node.matches(selector));
      return isEntering || isVisiblePurchaseSurface(node);
    }

    function hasBlockingPurchaseSurface() {
      return Array.from(
        document.querySelectorAll(PURCHASE_SURFACE_TARGET_SELECTOR),
      ).some(isBlockingPurchaseSurface);
    }

    function syncPurchaseSurface() {
      state.purchaseSyncFrame = null;
      const blocked = hasBlockingPurchaseSurface();
      if (root.dataset.purchaseBlocked !== String(blocked)) {
        root.dataset.purchaseBlocked = String(blocked);
      }
      if (openButton.disabled !== blocked) openButton.disabled = blocked;
      if (blocked) {
        if (openButton.getAttribute("aria-hidden") !== "true") {
          openButton.setAttribute("aria-hidden", "true");
        }
        if (
          root.dataset.open === "true" ||
          openButton.getAttribute("aria-expanded") === "true"
        ) {
          close(false);
        }
      } else if (openButton.hasAttribute("aria-hidden")) {
        openButton.removeAttribute("aria-hidden");
      }
    }

    function schedulePurchaseSurfaceSync() {
      if (state.purchaseSyncFrame !== null) return;
      state.purchaseSyncFrame = window.requestAnimationFrame(syncPurchaseSurface);
    }

    function handlePurchaseSurfaceTransition(event) {
      const target = event.target;
      if (!(target instanceof Element) || !target.matches(PURCHASE_SURFACE_TARGET_SELECTOR)) return;
      schedulePurchaseSurfaceSync();
    }

    function open() {
      if (root.dataset.purchaseBlocked === "true" || hasBlockingPurchaseSurface()) {
        syncPurchaseSurface();
        return;
      }
      if (state.closeTimer) window.clearTimeout(state.closeTimer);
      state.lastFocused = document.activeElement;
      loadSupportImages();
      panel.hidden = false;
      backdrop.hidden = false;
      openButton.setAttribute("aria-expanded", "true");
      syncModalState();
      window.requestAnimationFrame(() => {
        root.dataset.open = "true";
        if (titleNode) titleNode.focus({ preventScroll: true });
        flushDisplayed();
      });
    }

    function close(restoreFocus = true) {
      stopReading();
      root.dataset.open = "false";
      openButton.setAttribute("aria-expanded", "false");
      syncModalState();
      root.dataset.keyboardOpen = "false";
      state.closeTimer = window.setTimeout(() => {
        panel.hidden = true;
        backdrop.hidden = true;
      }, 190);
      if (restoreFocus && state.lastFocused && typeof state.lastFocused.focus === "function") {
        state.lastFocused.focus({ preventScroll: true });
      }
    }

    function handlePanelKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      if (panel.getAttribute("aria-modal") !== "true") return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function updateKeyboardState() {
      if (!window.visualViewport || !window.matchMedia("(max-width: 699px)").matches) {
        root.dataset.keyboardOpen = "false";
        return;
      }
      root.dataset.keyboardOpen = String(window.visualViewport.height < window.innerHeight * 0.72);
    }

    const purchaseSurfaceObserver = new MutationObserver(schedulePurchaseSurfaceSync);
    purchaseSurfaceObserver.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });
    document.addEventListener("transitionend", handlePurchaseSurfaceTransition, true);
    document.addEventListener("transitioncancel", handlePurchaseSurfaceTransition, true);

    openButton.addEventListener("click", open);
    closeButtons.forEach((button) => button.addEventListener("click", close));
    if (minimizeButton) minimizeButton.addEventListener("click", close);
    panel.addEventListener("keydown", handlePanelKeydown);
    form.addEventListener("submit", handleSubmit);

    showProductButtons.forEach((button) =>
      button.addEventListener("click", () => showProductPicker()),
    );
    hideProductButtons.forEach((button) => button.addEventListener("click", hideProductPicker));
    productChoices.forEach((choice) => choice.addEventListener("click", () => selectProduct(choice)));
    if (currentProduct) currentProduct.addEventListener("click", () => selectProduct(currentProduct));
    if (addImageButton && fileInput) {
      addImageButton.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", () => attachProductImage(fileInput.files && fileInput.files[0]));
    }
    if (removeImageButton) removeImageButton.addEventListener("click", clearAttachment);
    input.addEventListener("paste", (event) => {
      const items = event.clipboardData && Array.from(event.clipboardData.items || []);
      const imageItem = items && items.find((item) => item.kind === "file" && item.type.startsWith("image/"));
      if (!imageItem) return;
      event.preventDefault();
      attachProductImage(imageItem.getAsFile());
    });
    window.addEventListener("beforeunload", () => {
      stopReading();
      if (window.speechSynthesis) window.speechSynthesis.removeEventListener("voiceschanged", updateReadingControls);
      if (state.attachmentObjectUrl) URL.revokeObjectURL(state.attachmentObjectUrl);
      purchaseSurfaceObserver.disconnect();
      document.removeEventListener("transitionend", handlePurchaseSurfaceTransition, true);
      document.removeEventListener("transitioncancel", handlePurchaseSurfaceTransition, true);
    });
    window.addEventListener("pagehide", stopReading);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopReading();
      else window.requestAnimationFrame(flushDisplayed);
    });
    if (window.speechSynthesis) window.speechSynthesis.addEventListener("voiceschanged", updateReadingControls);
    input.addEventListener("input", updateComposer);
    input.addEventListener("keydown", (event) => {
      const desktop = !window.matchMedia("(max-width: 699px)").matches;
      if (desktop && event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    suggestions.addEventListener("click", (event) => {
      const button = event.target.closest("[data-aw-ai-question]");
      if (button) submitText(button.dataset.awAiQuestion);
    });
    window.addEventListener("online", refreshConnection);
    window.addEventListener("offline", refreshConnection);
    window.addEventListener(
      "resize",
      () => {
        syncModalState();
        updateKeyboardState();
        schedulePurchaseSurfaceSync();
        if (root.dataset.open === "true") loadSupportImages();
      },
      { passive: true },
    );
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateKeyboardState);
    }

    mountRecordingControls();
    updateComposer();
    refreshConnection();
    updateKeyboardState();
    syncPurchaseSurface();
  }

  function boot(scope = document) {
    if (scope.matches && scope.matches("[data-anywell-customer-service]")) init(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll("[data-anywell-customer-service]").forEach(init);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }

})();
